// Screen B - the data room (docs/design/slice4-certify-ux.md §5). List + upload. The
// platform's FIRST sensitive-upload path - the "stored privately/encrypted" affordance
// renders BEFORE any file is chosen (§5.1), and the upload is never drag-only (a real
// <label>-associated file input is the keyboard/screen-reader path, WCAG 2.5.7).
import { useEffect, useId, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { Certificate } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import { listCertificates, uploadCertificate } from "../../api/certify.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { evaluateCertifyGate } from "../../certify/gate.js";
import { certifyCopy } from "../../copy/certify.js";
import { CertifyTierRefusalCard } from "../../components/CertifyTierRefusalCard.js";
import { LifecycleGateCard } from "../../components/LifecycleGateCard.js";
import { CredentialCard } from "../../components/CredentialCard.js";

// Illustrative caps (ASSUMED, flagged - no canon doc pins a confirmed value for the
// certificate-upload size/type limits this slice; `certify.errors.certificate
// .tooLarge`'s `{maxSizeMb}` is a system-computed placeholder, never a hardcoded
// figure per the copy file's own instruction, so this constant is this lane's own
// reasonable default pending a confirmed limit, not a source of truth).
const MAX_SIZE_MB = 10;
const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

type ListState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly certificates: readonly Certificate[] };

type UploadState =
  | { readonly status: "idle" }
  | { readonly status: "dragging" }
  | { readonly status: "selected"; readonly file: File }
  | { readonly status: "rejected"; readonly reason: "type" | "size" }
  | { readonly status: "uploading"; readonly file: File }
  | { readonly status: "rejected_post_scan" }
  | { readonly status: "stored"; readonly certificate: Certificate };

export const DataRoom = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const navigate = useNavigate();
  const { state: session } = useSession();
  const account = session.status === "ready" ? session.account : null;
  const canManage = isOwnerOrAdmin(session);
  const gate = evaluateCertifyGate(app, account);
  const entitled = !gate.tierDenied && !gate.stageDenied;

  const [listState, setListState] = useState<ListState>({ status: "loading" });
  const [uploadState, setUploadState] = useState<UploadState>({ status: "idle" });
  const [issuer, setIssuer] = useState("");
  const [scope, setScope] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [attempt, setAttempt] = useState(0);
  const fileInputId = useId();

  useEffect(() => {
    if (!entitled) return;
    const cancelled = { current: false };
    setListState({ status: "loading" });
    void (async () => {
      const result = await listCertificates(appId);
      if (cancelled.current) return;
      setListState(
        isErr(result) ? { status: "error" } : { status: "ready", certificates: result.value.certificates },
      );
    })();
    return () => {
      cancelled.current = true;
    };
  }, [entitled, appId, attempt]);

  if (gate.tierDenied) {
    return (
      <div className="wrap">
        <CertifyTierRefusalCard appName={app.name} stageDenied={gate.stageDenied} onAskAboutUpgrading={() => undefined} />
      </div>
    );
  }
  if (gate.stageDenied) {
    return (
      <div className="wrap">
        <LifecycleGateCard appName={app.name} />
      </div>
    );
  }

  const validateAndSelect = (file: File): void => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUploadState({ status: "rejected", reason: "type" });
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadState({ status: "rejected", reason: "size" });
      return;
    }
    setUploadState({ status: "selected", file });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file !== undefined) validateAndSelect(file);
  };

  const handleUpload = (): void => {
    if (uploadState.status !== "selected" || issuer.trim() === "" || scope.trim() === "" || expiresAt === "") return;
    const file = uploadState.file;
    setUploadState({ status: "uploading", file });
    void (async () => {
      const result = await uploadCertificate(appId, {
        issuer,
        scope,
        issuedAt: issuedAt === "" ? new Date().toISOString() : new Date(issuedAt).toISOString(),
        expiresAt: new Date(expiresAt).toISOString(),
        file,
      });
      if (isErr(result)) {
        setUploadState({ status: "rejected_post_scan" });
        return;
      }
      setUploadState({ status: "stored", certificate: result.value });
      setListState((prev) => ({
        status: "ready",
        certificates: prev.status === "ready" ? [result.value, ...prev.certificates] : [result.value],
      }));
    })();
  };

  return (
    <div className="wrap">
      <h1 className="h2">{certifyCopy("certify.dataRoom.heading")}</h1>
      <p className="muted mt8">{certifyCopy("certify.dataRoom.subtitle", { appName: app.name })}</p>

      {canManage ? (
        <div className="card pad mt16" data-testid="upload-dropzone">
          {/* The privacy affordance renders BEFORE any file is chosen, standing and
              non-collapsible (§5.1) - real text, not an icon alone. */}
          <div className="well pad row aic gap8" data-testid="privacy-affordance">
            <span aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="5" y="11" width="14" height="9" rx="1.5" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </span>
            <span style={{ fontSize: 12.5 }}>{certifyCopy("certify.dataRoom.privacyNotice")}</span>
          </div>

          {uploadState.status === "idle" || uploadState.status === "dragging" || uploadState.status === "rejected" ? (
            <div
              className={`dropzone mt12${uploadState.status === "dragging" ? " dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setUploadState({ status: "dragging" });
              }}
              onDragLeave={() => {
                setUploadState({ status: "idle" });
              }}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file !== undefined) validateAndSelect(file);
              }}
            >
              <p className="muted" style={{ fontSize: 13 }}>
                {uploadState.status === "dragging"
                  ? certifyCopy("certify.dataRoom.dragOver")
                  : certifyCopy("certify.dataRoom.dropInstruction")}
              </p>
              <label className="btn btn-ghost btn-sm" htmlFor={fileInputId}>
                {certifyCopy("certify.dataRoom.chooseFileCta")}
              </label>
              <input
                id={fileInputId}
                type="file"
                accept={ACCEPTED_TYPES.join(",")}
                className="sr-only"
                onChange={handleFileChange}
              />
              {uploadState.status === "rejected" ? (
                <p className="field-error mt8" role="alert">
                  {uploadState.reason === "type"
                    ? certifyCopy("certify.errors.certificate.wrongFileType")
                    : certifyCopy("certify.errors.certificate.tooLarge", { maxSizeMb: MAX_SIZE_MB })}
                </p>
              ) : null}
            </div>
          ) : null}

          {uploadState.status === "selected" || uploadState.status === "uploading" ? (
            <div className="mt12" data-testid="certificate-metadata-form">
              <p style={{ fontSize: 13, fontWeight: 600 }}>{uploadState.file.name}</p>
              <p className="muted" style={{ fontSize: 12 }}>{Math.ceil(uploadState.file.size / 1024)} KB</p>
              <div className="stack gap8 mt8">
                <label className="field-label" htmlFor="certificate-issuer">
                  {certifyCopy("certify.dashboard.certificate.field.issuer")}
                </label>
                <input
                  id="certificate-issuer"
                  className="field"
                  value={issuer}
                  disabled={uploadState.status === "uploading"}
                  onChange={(e) => { setIssuer(e.target.value); }}
                />
                <label className="field-label" htmlFor="certificate-scope">
                  {certifyCopy("certify.dashboard.certificate.field.scope")}
                </label>
                <input
                  id="certificate-scope"
                  className="field"
                  value={scope}
                  disabled={uploadState.status === "uploading"}
                  onChange={(e) => { setScope(e.target.value); }}
                />
                <label className="field-label" htmlFor="certificate-issued">
                  {certifyCopy("certify.dashboard.certificate.field.issuedDate")}
                </label>
                <input
                  id="certificate-issued"
                  type="date"
                  className="field"
                  value={issuedAt}
                  disabled={uploadState.status === "uploading"}
                  onChange={(e) => { setIssuedAt(e.target.value); }}
                />
                <label className="field-label" htmlFor="certificate-expiry">
                  {certifyCopy("certify.dashboard.certificate.field.expiryDate")}
                </label>
                <input
                  id="certificate-expiry"
                  type="date"
                  className="field"
                  value={expiresAt}
                  disabled={uploadState.status === "uploading"}
                  onChange={(e) => { setExpiresAt(e.target.value); }}
                />
                {expiresAt !== "" && new Date(expiresAt).getTime() <= Date.now() ? (
                  <p className="field-error" role="alert">
                    {certifyCopy("certify.errors.certificate.alreadyExpired")}
                  </p>
                ) : null}
              </div>

              {uploadState.status === "uploading" ? (
                <div className="mt12" data-testid="upload-progress">
                  <div className="meter bordered">
                    <span style={{ width: "70%" }} />
                  </div>
                  <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
                    {certifyCopy("certify.dataRoom.uploading", { filename: uploadState.file.name })}
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary mt12"
                  disabled={issuer.trim() === "" || scope.trim() === "" || expiresAt === ""}
                  onClick={handleUpload}
                >
                  {certifyCopy("certify.dashboard.certificate.uploadCta")}
                </button>
              )}
            </div>
          ) : null}

          {uploadState.status === "rejected_post_scan" ? (
            <div className="mt12" data-testid="post-scan-rejected">
              <p className="field-error" role="alert">
                {certifyCopy("certify.errors.certificate.failedScan")}
              </p>
              <button
                type="button"
                className="btn btn-ghost btn-sm mt8"
                onClick={() => {
                  setUploadState({ status: "idle" });
                }}
              >
                Try again
              </button>
            </div>
          ) : null}

          {uploadState.status === "stored" ? (
            <div className="banner good mt12" role="status" data-testid="upload-success">
              <span className="grow">
                {certifyCopy("certify.dataRoom.storedUnverified")}{" "}
                {certifyCopy("certify.dataRoom.addedConfirmation", { filename: uploadState.certificate.issuer })}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt16">
        {listState.status === "loading" ? (
          <div className="stack gap12">
            <div className="card skeleton-card" />
            <div className="card skeleton-card" />
          </div>
        ) : listState.status === "error" ? (
          <div className="card state-card">
            <p>We could not load the data room. Try again.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setAttempt((a) => a + 1);
              }}
            >
              Retry
            </button>
          </div>
        ) : listState.certificates.length === 0 ? (
          <div className="empty-state" data-testid="data-room-empty">
            <p className="muted">{certifyCopy("certify.empty.dataRoom.firstRun.heading")}</p>
            <p className="muted">{certifyCopy("certify.empty.dataRoom.firstRun.body", { appName: app.name })}</p>
          </div>
        ) : (
          <div className="stack gap12" data-testid="credential-list">
            {listState.certificates.map((certificate) => (
              <CredentialCard
                key={certificate.fileRef}
                certificate={certificate}
                appName={app.name}
                onOpen={() => {
                  void navigate(`/apps/${appId}/certify/data-room/${encodeURIComponent(certificate.fileRef)}`);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
